import React, { useState, useEffect, useCallback, memo } from 'react'
import storyService, { Story } from '../../services/storyService'
import { ChevronLeft, ChevronRight, X, Plus, Camera } from 'lucide-react'
import { cn } from '../../lib/utils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

// Story Card Component - Memoized for performance
const StoryCard = memo(({ story, index, onClick }: { story: Story; index: number; onClick: () => void }) => (
  <div
    className="relative aspect-[9:16] rounded-xl overflow-hidden cursor-pointer group"
    onClick={onClick}
  >
    {story.mediaType === 'VIDEO' ? (
      <video
        src={story.mediaUrl}
        className="w-full h-full object-cover"
        muted
        preload="metadata"
      />
    ) : (
      <img
        src={story.mediaUrl}
        alt={story.caption || 'Story'}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
    <div className="absolute top-2 left-2">
      <div className={cn(
        "w-3 h-3 rounded-full border-2 border-white",
        story.hasViewed ? "bg-gray-400" : "bg-blue-600"
      )} />
    </div>
    <div className="absolute bottom-2 left-2 right-2">
      <div className="flex items-center gap-2">
        <img
          src={story.user.profileImage || '/default-avatar.svg'}
          alt={story.user.firstName}
          className="w-8 h-8 rounded-full object-cover border-2 border-white"
          loading="lazy"
        />
        <div className="text-white text-sm font-medium truncate">
          {story.user.firstName} {story.user.lastName}
        </div>
      </div>
    </div>
    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />
  </div>
))

StoryCard.displayName = 'StoryCard'

// Story Progress Bar - Memoized
const StoryProgressBar = memo(({ progress, total, current }: { progress: number; total: number; current: number }) => (
  <div className="absolute top-2 left-2 right-2 flex gap-1 z-10">
    {Array.from({ length: total }, (_, i) => (
      <div key={i} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full bg-white transition-all",
            i < current ? "w-full" : i === current ? "" : "w-0"
          )}
          style={i === current ? { width: `${progress}%` } : {}}
        />
      </div>
    ))}
  </div>
))

StoryProgressBar.displayName = 'StoryProgressBar'

// Story Viewer Modal - Memoized
const StoryViewerModal = memo<{
  stories: Story[]
  activeIndex: number
  progress: number
  onClose: () => void
  onNext: () => void
  onPrev: () => void
}>(({ stories, activeIndex, progress, onClose, onNext, onPrev }) => {
  const story = stories[activeIndex]

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-4 right-4 text-white z-10 p-2">
        <X className="w-8 h-8" />
      </button>

      {/* Progress Bar */}
      <StoryProgressBar progress={progress} total={stories.length} current={activeIndex} />

      {/* Navigation */}
      {activeIndex > 0 && (
        <button
          onClick={onPrev}
          className="absolute left-4 text-white p-2 rounded-full bg-black/50 hover:bg-black/70 z-10"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      <button
        onClick={onNext}
        className="absolute right-4 text-white p-2 rounded-full bg-black/50 hover:bg-black/70 z-10"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Story Content */}
      <div className="w-full max-w-md h-full max-h-[80vh] relative">
        {story.mediaType === 'VIDEO' ? (
          <video
            src={story.mediaUrl}
            className="w-full h-full object-contain"
            autoPlay
            muted
            loop
          />
        ) : (
          <img
            src={story.mediaUrl}
            alt={story.caption || 'Story'}
            className="w-full h-full object-contain"
          />
        )}
        {story.caption && (
          <div className="absolute bottom-4 left-4 right-4 text-white text-center bg-black/40 py-2 px-3 rounded-lg">
            {story.caption}
          </div>
        )}
      </div>
    </div>
  )
})

StoryViewerModal.displayName = 'StoryViewerModal'

// Loading Skeleton - Memoized
const LoadingSkeleton = memo(() => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
    {Array.from({ length: 4 }, (_, i) => (
      <div key={i} className="aspect-[9:16] rounded-xl bg-gray-200 animate-pulse" />
    ))}
  </div>
))

LoadingSkeleton.displayName = 'LoadingSkeleton'

// Main Stories Page Component
const StoriesPage = () => {
  const [stories, setStories] = useState<Story[]>([])
  const [loading, setLoading] = useState(true)
  const [activeStoryIndex, setActiveStoryIndex] = useState<number | null>(null)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    fetchStories()
  }, [])

  const fetchStories = async () => {
    try {
      const response = await storyService.getFeedStories()
      if (response.success) {
        setStories(response.data)
      }
    } catch (error) {
      console.error('Error fetching stories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewStory = useCallback(async (storyId: number) => {
    try {
      await storyService.viewStory(storyId)
    } catch (error) {
      console.error('Error viewing story:', error)
    }
  }, [])

  const handleStoryClick = useCallback((index: number) => {
    setActiveStoryIndex(index)
    setProgress(0)
    handleViewStory(stories[index].id)
  }, [stories])

  const handleClose = useCallback(() => {
    setActiveStoryIndex(null)
    setProgress(0)
  }, [])

  const handleNext = useCallback(() => {
    if (activeStoryIndex !== null && activeStoryIndex < stories.length - 1) {
      setActiveStoryIndex(activeStoryIndex + 1)
      setProgress(0)
      handleViewStory(stories[activeStoryIndex + 1].id)
    } else {
      handleClose()
    }
  }, [activeStoryIndex, stories, handleViewStory, handleClose])

  const handlePrev = useCallback(() => {
    if (activeStoryIndex !== null && activeStoryIndex > 0) {
      setActiveStoryIndex(activeStoryIndex - 1)
      setProgress(0)
      handleViewStory(stories[activeStoryIndex - 1].id)
    }
  }, [activeStoryIndex, stories, handleViewStory])

  // Progress timer
  useEffect(() => {
    if (activeStoryIndex !== null) {
      const timer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            handleNext()
            return 0
          }
          return prev + 2
        })
      }, 100)
      return () => clearInterval(timer)
    }
  }, [activeStoryIndex, handleNext])

  // Keyboard navigation
  useEffect(() => {
    if (activeStoryIndex === null) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
      if (e.key === 'ArrowLeft') handlePrev()
      if (e.key === 'ArrowRight') handleNext()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeStoryIndex, handleClose, handlePrev, handleNext])

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle title="Stories" subtitle="View stories from your connections" icon={Camera} />

        {/* Create Story Button */}
        <GovCard>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Share Your Story</h3>
              <p className="text-sm text-gray-500">Create a story to share with your community</p>
            </div>
            <GovButton variant="primary" size="md">
              <Plus className="w-4 h-4 mr-1" />
              Create Story
            </GovButton>
          </div>
        </GovCard>

        {/* Stories Grid */}
        {loading ? (
          <LoadingSkeleton />
        ) : stories.length === 0 ? (
          <GovEmptyState
            icon="📸"
            title="No Stories Yet"
            description="Stories from people you follow will appear here"
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {stories.map((story, index) => (
              <StoryCard
                key={story.id}
                story={story}
                index={index}
                onClick={() => handleStoryClick(index)}
              />
            ))}
          </div>
        )}

        {/* Story Viewer Modal */}
        {activeStoryIndex !== null && stories.length > 0 && (
          <StoryViewerModal
            stories={stories}
            activeIndex={activeStoryIndex}
            progress={progress}
            onClose={handleClose}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}
      </div>
    </GovLayout>
  )
}

export default memo(StoriesPage)